import {zodResolver} from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {useToast} from '@/components/ui/use-toast';
import {useAuthorize} from '@/hooks/useAuth';
import {graphqlClient} from '@/services/graphqlClient';
import {useAddHealthLogMeasurementsMutation} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import {TIME_FORMATS} from '@/utils/timeFormats';
import {cn} from '@/utils/ui';

import type {HealthLogMeasurementFormValues} from '../utils/healthFormInput';
import {
  healthLogMeasurementFormDefaultValues,
  healthLogMeasurementFormSchema,
  mapHealthLogMeasurementFormValuesToMeasurementInput,
} from '../utils/healthFormInput';
import HealthLogMeasurementChartPreview from './HealthLogMeasurementChartPreview';
import HealthLogMeasurementFormFields from './HealthLogMeasurementFormFields';

export type AddHealthLogMeasurementDialogProps = DialogProps;

const AddHealthLogMeasurementDialog = ({
  onOpenChange,
  ...props
}: AddHealthLogMeasurementDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();
  const [searchParams] = useSearchParams();

  const canAddHealthLog = useAuthorize('CREATE_HEALTH_LOG_MEASUREMENT');

  const residentId = searchParams.get('residentId') || '';
  const metricId = searchParams.get('metricId') || '';
  const selectedDate =
    searchParams.get('date') || dayjs().format(TIME_FORMATS.dateTimeHTMLLocal);

  const onSuccess = () => {
    queryClient.invalidateQueries({queryKey: ['GetHealthLogMeasurements']});
  };

  const addMeasurements = useAddHealthLogMeasurementsMutation(graphqlClient, {
    onSuccess,
  });

  const form = useForm<HealthLogMeasurementFormValues>({
    resolver: zodResolver(healthLogMeasurementFormSchema),
    defaultValues: healthLogMeasurementFormDefaultValues({
      residentId,
    }),
  });

  // Watch for changes in form values
  const watchedResidentId = form.watch('residentId');
  const watchedMetricId = form.watch('metricId');

  const handleSubmit = async (values: HealthLogMeasurementFormValues) => {
    try {
      const measurementInput =
        mapHealthLogMeasurementFormValuesToMeasurementInput(values);

      await addMeasurements.mutateAsync({
        input: {
          measurements: [measurementInput],
        },
      });

      toast({
        title: t('Measurement added'),
        variant: 'success',
      });
      handleClose(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleClose = (value: boolean) => {
    form.reset();
    onOpenChange?.(value);
  };

  useEffect(() => {
    form.reset({
      residentId: residentId ?? '',
      recordedAt: dayjs(selectedDate || undefined).format(
        TIME_FORMATS.dateTimeHTMLLocal
      ),
      metricId: metricId ?? '',
    });
  }, [residentId, selectedDate, metricId, form]);

  const showChart = Boolean(watchedResidentId && watchedMetricId);

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild className={cn(showChart && 'md:max-w-6xl')}>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Add health log entry')}</DialogTitle>
            </DialogHeader>
            <DialogSection className={cn(showChart && 'md:flex-row md:gap-8')}>
              <div
                className={cn('flex flex-col gap-4', showChart && 'md:flex-1')}
              >
                <HealthLogMeasurementFormFields form={form} />
              </div>
              {showChart && (
                <HealthLogMeasurementChartPreview
                  residentId={watchedResidentId}
                  metricId={watchedMetricId}
                />
              )}
            </DialogSection>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange?.(false)}
              >
                {t('Cancel')}
              </Button>
              <Button
                disabled={!canAddHealthLog || addMeasurements.isPending}
                type="submit"
                variant="primary"
              >
                {t('Submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default AddHealthLogMeasurementDialog;
